import { Button, Checkbox, Group, List, Loader, Select, Stack, Text, Title } from "@mantine/core";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { to } from "shared/utils/fns";
import { graphql } from "src/gql";
import { Permission } from "src/gql/graphql";

const EditRoleForm = ({
    roleId,
    selectedPermissions,
    onChange,
}: {
    roleId: string;
    selectedPermissions: Permission[];
    onChange: (permission: Permission, op: "add" | "remove") => void;
}) => {
    const {
        data: { role },
    } = useSuspenseQuery({
        queryKey: ["roleId", roleId],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query RoleOMaticRoleId($roleId: UUID!) {
                        role(id: $roleId) {
                            id
                            description
                            name
                            type
                            permissions {
                                description
                                id
                                name
                                type
                            }
                        }
                    }
                `),
                { roleId },
            ),
    });

    return (
        <>
            <Text>
                <b>Description: </b>
                {role.description}
            </Text>
            <Text>
                <b>Type: </b>
                {role.type}
            </Text>
            <Text>
                <b>Permissions: ({role.permissions.length})</b>
            </Text>
            <Text fz="xs" fs="italic" c="dimmed">
                Note: roles/permissions are not necessarily identical between customers, use with caution
            </Text>
            <Stack flex={1} style={{ overflowY: "auto" }}>
                {Object.values(Permission).map((it) => (
                    <Checkbox
                        key={it}
                        label={it}
                        checked={selectedPermissions.some((p) => p == it)}
                        onChange={(e) => onChange(it as Permission, e.currentTarget.checked ? "add" : "remove")}
                    />
                ))}
            </Stack>
        </>
    );
};

const Page = () => {
    const [selectedRole, setSelectedRole] = useState<{ name: string; id: string; permissions: Permission[] } | null>(null);
    const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
    const [selectedPerms, setSelectedPerms] = useState<Permission[]>([]);

    const query_limit = 25;
    const customersQuery = useSuspenseQuery({
        queryKey: ["customers"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query RoleOMaticCustomers($limit: Int!) {
                        customers(offset: 0, limit: $limit) {
                            name
                            subdomain
                        }
                    }
                `),
                { limit: query_limit },
            ),
    });

    if (customersQuery.data.customers.length == query_limit) window.alert("Warning: customers query limit reached");

    const rolesQuery = useSuspenseQuery({
        queryKey: ["roles"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query RoleOMaticRoles($limit: Int!) {
                        roles(offset: 0, limit: $limit) {
                            id
                            name
                            permissions {
                                id
                            }
                        }
                    }
                `),
                { limit: query_limit },
            ),
    });

    if (rolesQuery.data.roles.length == query_limit) window.alert("Warning: roles query limit reached");

    useEffect(() => {
        setSelectedPerms(selectedRole?.permissions ?? []);
    }, [selectedRole]);

    const isStaging = import.meta.env.VITE_ENVIRONMENT != "prod";

    const addedPerms = selectedPerms.filter((it) => !selectedRole?.permissions.includes(it)) ?? [];
    const removedPerms = selectedRole?.permissions.filter((it) => !selectedPerms.includes(it)) ?? [];

    return (
        <Group m="xl" h="calc(100% - var(--mantine-spacing-xl) * 2)" align="flex-start" wrap="nowrap">
            <Stack mah="100%" w="40%" style={{ overflow: "hidden" }}>
                <Title order={2}>Edit role</Title>
                <Select
                    label="Select role"
                    placeholder="Select role..."
                    data={rolesQuery.data.roles.map((it) => ({ label: it.name, value: it.id }))}
                    onChange={(id) => {
                        const role = rolesQuery.data.roles.find((it) => it.id == id)!;
                        setSelectedRole({ name: role.name, id: role.id, permissions: role.permissions.map(to("id")) });
                    }}
                />
                {!!selectedRole && (
                    <Suspense fallback={<Loader />}>
                        <EditRoleForm
                            roleId={selectedRole.id}
                            selectedPermissions={selectedPerms}
                            onChange={(p, op) => {
                                if (op == "add") setSelectedPerms(selectedPerms.concat(p));
                                else setSelectedPerms(selectedPerms.filter((it) => it != p));
                            }}
                        />
                    </Suspense>
                )}
            </Stack>
            <Stack w="20%" h="100%" justify="center" align="center">
                <Title order={4}>Adding:</Title>
                <List>
                    {addedPerms.length == 0 ? (
                        <Text c="dimmed" fs="italic">
                            None
                        </Text>
                    ) : (
                        addedPerms.map((it) => (
                            <List.Item key={it} c="green">
                                {it}
                            </List.Item>
                        ))
                    )}
                </List>
                <Title order={4}>Removing:</Title>
                <List>
                    {removedPerms.length == 0 ? (
                        <Text c="dimmed" fs="italic">
                            None
                        </Text>
                    ) : (
                        removedPerms.map((it) => (
                            <List.Item key={it} c="red">
                                {it}
                            </List.Item>
                        ))
                    )}
                </List>
                <Text fz="sm" c="dimmed" style={{ textIndent: 15 }}>
                    Instructions: clicking the below button will open up a tab for each customer you select on the right. Once you login on
                    the tab, the permission changes listed above will be applied if possible.
                </Text>
                <Button
                    disabled={!selectedRole || (addedPerms.length == 0 && removedPerms.length == 0) || selectedCustomers.length == 0}
                    onClick={() => {
                        selectedCustomers.forEach((it) => {
                            const params = new URLSearchParams({
                                role: selectedRole!.name,
                                add: JSON.stringify(addedPerms),
                                remove: JSON.stringify(removedPerms),
                            });
                            const url = new URL(
                                `https://${it}.emns${isStaging ? ".staging" : ""}.bryx.com/devtools/role-o-matic/modify?${params.toString()}`,
                            );
                            window.open(url.toString(), "_blank");
                        });
                    }}
                    style={{ flexShrink: 0 }}
                >
                    Open tab(s)
                </Button>
                <Button
                    variant="outline"
                    onClick={() => {
                        setSelectedPerms(selectedRole?.permissions ?? []);
                        setSelectedCustomers([]);
                    }}
                >
                    Reset
                </Button>
            </Stack>
            <Stack mah="100%" w="40%" style={{ overflow: "hidden" }}>
                <Stack gap={0}>
                    <Title order={2}>Apply to customer(s):</Title>
                    <Text c="dimmed" fs="italic">
                        Selected: {selectedCustomers.length}
                    </Text>
                </Stack>
                <Group>
                    <Button flex={1} onClick={() => setSelectedCustomers(customersQuery.data.customers.map(to("subdomain")))}>
                        Select All
                    </Button>
                    <Button flex={1} variant="default" onClick={() => setSelectedCustomers([])}>
                        Clear
                    </Button>
                </Group>
                <Stack style={{ overflow: "auto" }}>
                    {customersQuery.data.customers.map((it) => (
                        <Checkbox
                            label={
                                <Group>
                                    <Text>{it.name}</Text>
                                    <Text fs="italic" c="dimmed">
                                        ({it.subdomain})
                                    </Text>
                                </Group>
                            }
                            checked={selectedCustomers.includes(it.subdomain)}
                            onChange={(e) =>
                                e.currentTarget.checked
                                    ? setSelectedCustomers(selectedCustomers.concat(it.subdomain))
                                    : setSelectedCustomers(selectedCustomers.filter((c) => it.subdomain != c))
                            }
                            key={it.subdomain}
                        />
                    ))}
                </Stack>
            </Stack>
        </Group>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/role-o-matic/")({
    component: () => (
        <Suspense fallback={<Loader />}>
            <Page />
        </Suspense>
    ),
});
